export enum ProviderType {
  ORGANIZATION = 'Organization',
  PRACTITIONER = 'Practitioner',
  PROVIDER_GROUP = 'Provider groups',
  MEDICAL_GROUP = 'Medical Group',
}
export enum ProviderTypeShortNames {
  ORGANIZATION = 'O',
  PRACTITIONER = 'P',
  PROVIDER_GROUP = 'PG',
  MEDICAL_GROUP = 'MG',
}

export interface ProviderBioData {
  biographyGenerated: boolean;
  cached: boolean;
  generatedBiography?: string;
  npi?: string;
  success?: boolean;
}

export interface ProviderBio {
  pk: string;
  sk: string;
  npi: string;
  data: ProviderBioData;
  timestamp: string;
  ttl: number;
}

export interface ProviderDetailsResponse {
  providerDetails: ProviderDetails;
  totalCount: number;
}

export interface AreaOfExpertiseDetails {
  name: string;
  description: string;
}

export interface ProviderDetails {
  areaOfExpertise?: string[];
  areaOfExpertiseDetails?: AreaOfExpertiseDetails[];
  biographicData?: string;
  boardCertifications: string[];
  culturalCompetencies: string[] | null;
  culturalCompetencyCategories?: CulturalCompetence[] | null;
  defaultTranslatedValues: DefaultTranslatedPractionerInfoProp;
  educationInfo: Education[];
  ethnicity?: string;
  gender: string;
  hospitalAffiliations: Identifiers[];
  imageLocation: string;
  languagesSpoken: string[];
  licenseNumber: string[];
  npi: string;
  primaryDegrees: string[];
  providerId: string;
  providerLocations: ProviderLocation[];
  providerName: string;
  providerType: ProviderType;
  specialties: string[];
  specialtiesWithId: SpecialtiesWithId[];
  pictureId: string;
  // Not available in ProviderDetails graphql schema
  UHPDRating?: string;
  accreditations?: string[];
  healthGradeRating?: string;
  lastUpdatedDate?: string;
  lastVerifiedDate?: string;
  networkStartDate?: string;
  networkStatus?: string;
  otherProviderDetails?: string[];
  otherSpecialities?: string[];
  primarySpeciality?: string[];
  professionalDesignation?: string;
  providerGroups?: string[];
  providerSpecialities?: string[];
  servicesProvided?: string[];
  organizationType?: string[];
  organizationCode?: string[];
  firstName?: string;
  middleName?: string;
  lastName?: string;
  surveyCount?: number;
  providerRace?: providerRace[];
  providerEthnicity?: providerEthnicity[];
  licensedStates?: string[];
  ascensionIndicator?: boolean;
  ascensionSchedulingLink?: string;
  providerBio?: ProviderBio;
  referralIndicator?: string[];
}
export interface RecommendationDetails {
  totalRecommendationScore?: string;
  quality: {
    scoreDetails?: ScoreDetails;
    credentialed?: string;
    premiumDesignation?: string;
    llmAffinity?: string;
    llmStatus?: string;
  };
  benefits: {
    scoreDetails?: ScoreDetails;
    preferredProviderStatus?: string;
    providerTierDesignation?: string;
    acoParticipationStatus?: string;
    costIndicator?: string;
    costEfficiencyRating?: string;
    costEfficiencyCode?: string;
  };
  convenience: {
    scoreDetails?: ScoreDetails;
    acceptingNewPatients?: string;
    appointmentAvailability?: string;
    eveningAppointments?: string;
    weekendAppointments?: string;
    onlineAppointmentScheduling?: string;
    distance?: string;
    memberPreferredRadius?: string;
    virtualAppointments?: string;
  };
  personalFit: {
    scoreDetails?: ScoreDetails;
    languageSpoken?: string;
    gender?: string;
    boardCertification?: string;
    consumerRating?: string;
    consumerRatingSurveyVolume?: string;
    consumerRatingMidPointStatus?: string;
  };
  verification: {
    scoreDetails?: ScoreDetails;
    officeLocationScore?: string;
    contactInfoScore?: string;
  };
  randomization: {
    scoreDetails?: ScoreDetails;
    randomizationKey?: string;
  };
  bandedCost?: string;
  billingCode?: string;
}
export interface ScoreDetails {
  value?: string;
  percentage?: string;
  match?: string;
}
export interface providerRace {
  raceLevel1: string;
  raceLevel2: string;
  raceLevel3: string;
}
export interface providerEthnicity {
  ethnicityLevel1: string;
  ethnicityLevel2: string;
  ethnicityLevel3: string;
}
export interface DefaultTranslatedPractionerInfoProp {
  areaOfExpertise: string[];
  areaOfExpertiseDetails?: AreaOfExpertiseDetails[];
  boardCertifications: string[];
  culturalCompetencies: string[];
  culturalCompetencyCategories?: CulturalCompetence[] | null;
  educationInfo: Education[];
  ethnicity: String;
  languagesSpoken: string[];
  primarySpecialities?: string[];
  specialties: string[];
  specialtiesWithId: SpecialtiesWithId[];
  licensedStates: string[];
}

export interface NetworkAffiliationDetails {
  networkAffiliationId: string;
  networkAffiliationName: string;
  acceptingNewPatients: boolean;
  pcpId?: string;
  staffModelIndicator?: boolean;
  roleCode?: string;
  networkIdentifier?: string;
  pcpIndicator?: boolean;
  providerRole?: string;
  specialities?: Array<{
    id: string;
    value: string;
  }>;
  medicalgrpaffilIdName?: string[];
  networkAffiliationTypeCode?: string;
  organizationLocationIdentifier?: string;
  decIdentifier?: string;
}

export interface GroupAffiliationDetails {
  groupId: string;
  groupName: string;
  affiliatedStartDate: string;
  affiliatedEndDate: string;
  pcpId?: string;
  staffModelIndicator?: boolean;
  networkIdentifier: string;
}

export interface NetworkDetails {
  networkAffiliations: NetworkAffiliationDetails[];
  groupAffiliations: GroupAffiliationDetails[];
}

export interface ProviderLocation {
  locationKey: string;
  costIndicator: string;
  efficiencyCode: string;
  qualityCode: string;
  tin: string;
  tty: string;
  digestId: string;
  pcpId: string;
  lastUpdatedDate: string;
  languagesSpokenByOffice: string[];
  languagesSpokenByInterpreters: string[];
  networkAffiliations: string[];
  networks: NetworkDetails;
  accessibility: string[];
  acoProvider: AcoProvider[];
  specialties: string[];
  specialtiesWithId: SpecialtiesWithId[];
  hasEveningAppointments: boolean;
  hasPCPServiceAvailability: boolean;
  hasWeekendAppointment: boolean;
  docasapPracticeId: string;
  isDocasapScheduled: boolean;
  docAsapMorningAvailabilityDate: string;
  docAsapAfterNoonAvailabilityDate: string;
  docAsapEveningAvailabilityDate: string;
  isExpressAccessProvider: boolean;
  isBccProvider: boolean;
  isGreenDiamondProvider: boolean;
  isPremiumCarePhysician: string;
  isTieredProvider: boolean;
  isTier2Provider?: boolean;
  isAcceptingMedicaid: boolean;
  isOxPreferredLab: boolean;
  defaultLocation: boolean;
  defaultTranslatedValues: DefaultTranslatedPractitionerLocationProp;
  network: Network;
  practiceLimitations: PracticeLimitations;
  locationId: string;
  locationAvailability?: string;
  facilityProgramIds: string[];
  platinumPrograms: string[];
  acceptingNewPatients: boolean | null;
  accommodates?: Accommodates;
  address: Address;
  distance: string;
  emailAddress: string[];
  hoursOfOperation: HoursOfOperation[];
  latitude: string;
  longitude: string;
  virtualCareOffered: string[];
  phones: PhoneNumbers;
  website: string;
  websiteList: string[];
  // Not available in ProviderDetails graphql schema
  ddpCode?: string;
  medicalEquipments?: string[];
  facilityProviderSpecialties?: FacilityProviderSpecialities;
  providerGroupsAffiliations: ProviderGroupsAffiliations[];
  leapFrogRatings?: LeapFrogRating;
  servicesOffered?: string[];
  medicalInterpreterInOffice?: boolean;
  languageLineAvailable?: boolean;
  methodsOfService?: string[];
  publicTransportationMethods?: string[];
  isSedationOffered: boolean;
  electronicPrescribingOffered: boolean;
  isPCMH?: boolean;
  isTexasHealthStepProvider?: boolean;
  dbaName?: string;
  isECP?: boolean;
  hospitalBasedProviderIndicator?: boolean;
  hcbsService: HcbsService;
  amhId?: string;
  amhTierType?: string;
  recommendationDetails?: RecommendationDetails;
  virtualIndicator?: string;
  ascensionIndicator?: boolean;
  ascensionSchedulingLink?: string;
}

export interface Accommodates {
  acceptingChildrenWithSpecialHealthCareNeeds: boolean;
  treatsChildrenUnderFive: boolean;
  treatsIntellectuallyDisabledPatients: boolean;
  treatsAdultSpecialityNeedsPatients: boolean;
  treatsTMJ: boolean;
  treatsWheelChairBoundPatients: boolean;
}

export interface DefaultTranslatedPractitionerLocationProp {
  accessibility?: string[];
  languagesSpokenByOffice?: string[];
  languagesSpokenByInterpreters?: string[];
  practiceLimitations: PracticeLimitations;
  specialties?: string[];
  specialtiesWithId: SpecialtiesWithId[];
  virtualCareOffered?: string[];
}

export interface Education {
  schoolName: string;
  degrees: Degrees[];
}

export interface Degrees {
  degreeCode: string;
  isPrimary: boolean;
  description: string;
}

export interface Identifiers {
  id: string;
  value: string;
}

/** /////////////////////////////////////////////////////////////////////////////////////////////// */

/**
 * SHARED INTERFACES
 * Interfaces shared among providerDetails.ProviderLocations[], facilityDetails.FacilityLocationsResponse[], provider_group Details and medical_group Details locations arrays
 * - LeapFrogRatings
 * - Network
 * - Address
 * - PhoneNumbers
 * - FacilityProviderSpecialities
 * - HoursOfOperation
 * - PracticeLimitations
 */

export interface HoursOfOperation {
  day: string;
  openingTime: string;
  closingTime: string;
}

export interface Address {
  line: string[];
  city: string;
  district?: string;
  state: string;
  postalCode: string;
  locationName: string;
}

export interface AcoProvider {
  acoNetworkDescription?: string;
  acoNetworkId?: string;
}

export interface PhoneNumbers {
  phone?: string[];
  emergency?: string[];
  appointment?: string[];
  fax?: string[];
  afterHours?: string[];
}

export interface Network {
  start: string;
  end: string;
  status: string;
}

export interface PracticeLimitations {
  age: string;
  gender: string;
}

export interface FacilityProviderSpecialities {
  hasAnesthesiology: boolean;
  hasEmergencyMedicine: boolean;
  isFreeStandingFacility: boolean;
  hasPathology: boolean;
  hasRadiology: boolean;
  hasJCAHOAccreditation: boolean;
  isPreferredLabNetwork: boolean;
  hasNeonatology: Boolean;
  hasHospitalist: Boolean;
  hasIntensivist: Boolean;
}

export interface LeapFrogRating {
  rawValue: string;
  displayField: string;
  ratingValue: string;
}

export interface ProviderGroupsAffiliations {
  id: string;
  value: string;
}

export interface GetProviderDetails {
  providerDetails: {
    providerName: string;
    providerId: string;
    providerLocations: {
      address: string;
      acceptingNewPatients: boolean;
      distance: string;
    }[];
  };
}

export interface SpecialtiesWithId {
  id: String;
  label: String;
}

export interface CulturalCompetence {
  category: String;
  description: String;
}

export interface HcbsService {
  serviceCategory: string[];
  waivers: string[];
  countyServices: string[];
}
